import React, { useCallback, useContext, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Controller, useFormContext } from 'react-hook-form';
import { Grid } from '@nextiva/next-ui-components';
import { debounce } from 'lodash';

import { PlatformDialog } from 'components/Dialog';
import { Hint } from 'components/PhoneNumberHint';
import { RevealCard } from 'components/RevealCard';
import { actionTypes, CallForwardContext } from 'containers/CallForwarding';
import { HuntGroupService } from 'services';
import {
  RadioGrp,
  Label,
  RadioBtn,
  ForwardFormControl,
  ForwardGrid,
  CallForwardGrid,
  PhoneNumberText,
  UnansweredSeconds,
} from './CallForwardUnanswered.styles';

const UNANSWERED_ENABLED = 'unanswered.enabled';
const UNANSWERED_PHONE_NUMBER = 'unanswered.forwardToPhoneNumber';
const UNANSWERED_SECONDS = 'unanswered.forwardTimeoutSeconds';

export const CallForwardUnanswered = ({ huntGroupId }) => {
  const [modal, setModal] = useState({ isLoading: false, open: false });
  const { state, dispatch } = useContext(CallForwardContext);

  const {
    control,
    getValues,
    clearErrors,
    setError,
    setValue,
    watch,
  } = useFormContext();
  const forwardToVoicemail = watch('unanswered.forwardToVoicemail');
  const debouncedSetUnansweredDirty = useMemo(
    () =>
      debounce(
        (value) => {
          const dirty = !Object.values(
            state.originalFields.unanswered,
          ).includes(value);
          if (value === 'true') clearErrors(UNANSWERED_PHONE_NUMBER);
          if (dirty === state.dirtySections.unanswered) return; // avoid re-rendering if dirty is unchanged

          dispatch({
            type: actionTypes.DIRTY_UPDATED,
            payload: { sectionName: 'unanswered', dirty },
          });
        },
        200,
        { trailing: true },
      ),
    [state.originalFields.unanswered, state.dirtySections.unanswered, dispatch],
  );

  const updateActiveStatus = useCallback(() => {
    const checked = !getValues(UNANSWERED_ENABLED);
    const phoneNumber = getValues(UNANSWERED_PHONE_NUMBER);

    if (checked && !phoneNumber) {
      setError(
        UNANSWERED_PHONE_NUMBER,
        { type: 'required', message: 'Phone number is required.' },
        { shouldFocus: true },
      );
      return false;
    }
    if (!checked && phoneNumber) {
      setError(
        UNANSWERED_PHONE_NUMBER,
        {
          type: 'required',
          message: 'Remove phone number and click save to turn off.',
        },
        { shouldFocus: true },
      );
      return false;
    }
    if ((checked && phoneNumber) || (!checked && !phoneNumber)) {
      clearErrors(UNANSWERED_PHONE_NUMBER);
    }

    dispatch({
      type: actionTypes.SWITCH_TOGGLED,
      payload: { sectionName: 'unanswered', enabled: checked },
    });
  }, [dispatch, clearErrors, getValues, setError]);

  const handleMissingFields = () => {
    const phoneNumber = getValues(UNANSWERED_PHONE_NUMBER);
    const payload = phoneNumber
      ? {
          message:
            'You have unsaved changes, click the blue save button to enable this feature.',
          section: 'unanswered',
          type: 'error',
        }
      : {
          message:
            'Missing required information.Fill out the missing fields and then click the blue "Save" button to enable this feature.',
          section: 'unanswered',
          type: 'error',
        };

    dispatch({ type: actionTypes.TOGGLE_FAILED, payload });
  };

  const handleSwitchClick = ({ event, field }) => {
    const forwardToVoicemail =
      getValues('unanswered.forwardToVoicemail') === 'true';

    if (field.value === true && forwardToVoicemail) {
      setModal({ isLoading: false, open: true });
      return;
    }

    updateActiveStatus();
    if (!forwardToVoicemail) {
      handleMissingFields();
      return;
    }

    field.onChange(event);
  };

  const confirmToggleOff = async () => {
    setModal({ isLoading: true, open: true });

    try {
      const newUnanswered = await HuntGroupService.updateCallForwardUnanswered(
        huntGroupId,
        {
          forwardToVoicemail: false,
          forwardToPhoneNumber: '',
          forwardTimeoutSeconds: 0,
          forwardAfterTimeout: true,
        },
      );

      dispatch({
        type: actionTypes.UNANSWERED_CHANGES_SAVED,
        payload: newUnanswered,
      });
      setModal({ isLoading: false, open: false });
    } catch {
      const payload = {
        message:
          'There was an error while saving call forwarding information for this team. Please try again later or contact support if the issue persists.',
        section: 'unanswered',
        type: 'error',
      };

      dispatch({ type: actionTypes.TOGGLE_FAILED, payload });
    }
  };
  // This fuction clears sets the value of phonenumbers and seconds based on the checked status of forward to voicemail or forward to number.
  const handleSelectChange = (isForwardToVoicemail, checked) => {
    if (
      checked &&
      isForwardToVoicemail &&
      state.originalFields.unanswered?.forwardToPhoneNumber
    ) {
      setValue(
        UNANSWERED_PHONE_NUMBER,
        state.originalFields.unanswered?.forwardToPhoneNumber,
      );
      setValue(
        UNANSWERED_SECONDS,
        state.originalFields.unanswered?.forwardTimeoutSeconds,
      );
    } else {
      setValue(UNANSWERED_PHONE_NUMBER, '');
      setValue(UNANSWERED_SECONDS, 7);
    }
  };

  const forwardToVoicemailChangeHandler = (event) => {
    const isForwardToVoicemail =
      state.originalFields.unanswered?.forwardToVoicemail;
    handleSelectChange(isForwardToVoicemail, event.target.checked);
  };

  const forwardToNumberChangeHandler = (event) => {
    const isForwardToVoicemail = !state.originalFields.unanswered
      ?.forwardToVoicemail;
    handleSelectChange(isForwardToVoicemail, event.target.checked);
  };

  const cancelToggleOff = () => {
    setModal({ isLoading: false, open: false });
  };

  return (
    <RevealCard
      description="Forward incoming calls to a different team's number when your phone is unanswered."
      handleSwitchClick={handleSwitchClick}
      sectionName="unanswered"
      title="Call forward (unanswered)"
      tooltip="Forward all the team's incoming calls to a different phone number if not answered within a specified number of seconds."
    >
      <Grid container>
        <Grid className="field-container" item xs={12}>
          <ForwardFormControl component="fieldset">
            <Controller
              control={control}
              defaultValue="false"
              name="unanswered.forwardToVoicemail"
              render={({ field }) => {
                return (
                  <RadioGrp
                    {...field}
                    onChange={(event) => {
                      field.onChange(event);
                      debouncedSetUnansweredDirty(event.target.value);
                    }}
                    data-testid="PlatformAdminUI-TeamVoiceSettings-CallForwardUnanswered-RadioGroup"
                  >
                    <ForwardGrid className="field-container" item xs={5}>
                      <Label
                        value="true"
                        control={<RadioBtn disableRipple />}
                        onChange={forwardToVoicemailChangeHandler}
                        label="Forward to voicemail"
                        data-testid="PlatformAdminUI-TeamVoiceSettings-CallForwardUnanswered-ForwardToVoiceMailRadioButton"
                      />
                    </ForwardGrid>
                    <CallForwardGrid className="field-container" item xs={7}>
                      <Label
                        value="false"
                        control={<RadioBtn disableRipple />}
                        onChange={forwardToNumberChangeHandler}
                        label="Forward to number"
                        data-testid="PlatformAdminUI-TeamVoiceSettings-CallForwardUnanswered-ForwardToNumberRadioButton"
                      />
                    </CallForwardGrid>
                  </RadioGrp>
                );
              }}
            />
          </ForwardFormControl>
        </Grid>
        <Grid item xs={5}>
          <Controller
            control={control}
            defaultValue=""
            name={UNANSWERED_PHONE_NUMBER}
            render={({ field, fieldState }) => (
              <PhoneNumberText
                fullWidth
                id="unanswered-phone-number"
                label="PHONE NUMBER"
                variant="standard"
                inputProps={{ maxLength: 17 }}
                // eslint-disable-next-line react/jsx-no-duplicate-props
                InputProps={{ disableUnderline: true }}
                InputLabelProps={{ shrink: true }}
                {...field}
                onChange={(event) => {
                  clearErrors(UNANSWERED_PHONE_NUMBER);
                  field.onChange(event);
                  debouncedSetUnansweredDirty(event.target.value);
                }}
                error={Boolean(fieldState.error)}
                helperText={fieldState.error?.message}
                disabled={forwardToVoicemail === 'true'}
                data-testid="PlatformAdminUI-TeamVoiceSettings-CallForwardUnanswered-PhoneNumber"
              />
            )}
          />
        </Grid>
        <CallForwardGrid item xs={7}>
          <Hint
            label="SECONDS"
            tooltipText="Number of seconds call should ring before forwarding."
            shrink
          />
          <Controller
            control={control}
            defaultValue=""
            name={UNANSWERED_SECONDS}
            render={({ field, fieldState }) => (
              <UnansweredSeconds
                id="unanswered-seconds"
                inputProps={{ maxLength: 4 }}
                // eslint-disable-next-line react/jsx-no-duplicate-props
                InputProps={{ disableUnderline: true }}
                InputLabelProps={{ shrink: true }}
                {...field}
                onChange={(event) => {
                  clearErrors(UNANSWERED_SECONDS);
                  field.onChange(event);
                  debouncedSetUnansweredDirty(event.target.value);
                }}
                error={Boolean(fieldState.error)}
                helperText={fieldState.error?.message}
                data-testid="PlatformAdminUI-TeamVoiceSettings-CallForwardUnanswered-Seconds"
              />
            )}
          />
        </CallForwardGrid>
      </Grid>
      <PlatformDialog
        cancelButtonText="No"
        cancelClick={cancelToggleOff}
        header="Turn off voicemail?"
        isLoading={modal.isLoading}
        okButtonText="Yes"
        okClick={confirmToggleOff}
        open={modal.open}
        type="info"
      >
        This team’s unanswered calls are currently routed to voicemail. Are you
        sure you want to turn this off?
      </PlatformDialog>
    </RevealCard>
  );
};

CallForwardUnanswered.propTypes = {
  huntGroupId: PropTypes.string.isRequired,
};
